import React, { FC, useEffect, useRef, useState } from "react";
import { BackHandler, DeviceEventEmitter, FlatList, Image, Keyboard, KeyboardAvoidingView, Platform, Pressable, RefreshControl, TouchableOpacity, View, useWindowDimensions } from "react-native";
import { ThemeConsumer } from "@rneui/themed";
import Popover from "react-native-popover-view/dist/Popover";

import { useAppNavigation } from "../../../types/@react-navigation/navigation";
import { config } from "../../../shared/environment";

import { QuickEditSetStateType, QuickProductEdit } from "./quickProductEdit";
import PrintProductLabels from "./printProductLabels";

import CommonHeader from "../../../shared/commonComponents/commonHeader";
import CommonDialogBox from "../../../shared/commonComponents/commonDialogBox";

import { ProductDataProps, SortProps, CategoryBrandProps, DeleteProductResProps } from "./productModule";
import { bulkDeleteProductRedux, deleteProductRedux, getAllPosProductListRedux } from "../../../redux/slices/productSlice";
import { getScreenPermission, useAppDispatch, useAppSelector } from "../../../redux/hooks";

import { filterItemsProps } from "zen-core-elements/src/elements/zenFilterModal/zenFilterType.modal";
import { ZenBadge, ZenCheckBox, ZenEmptyComponent, ZenFilterModal, ZenIcon, ZenLoader, ZenText, ZenToast } from "zen-core-elements";
import { Code } from "../../customers/addEditCustomerModule";
import ZenSpeedDial, { DialActions } from "zen-core-elements/src/elements/zenSpeedDial/zenSpeedDial";
import { appColors } from "../../../themes/appTheme";
import { useKeyborad } from "../../../shared/hooks/useKeyborad";
import { ListToolbar } from "../../../shared/commonComponents/listToolbar";
import { shallowEqual } from "react-redux";
import { getAllPosCategoryData } from "../../../redux/slices/categorySlice";

//State variable to store limit value for list
const limit = 10
//Default sort of the product list ("Product name A-Z"), also used by the refetch after a product is added or edited
const DEFAULT_SORT = { id: 1, field: 'name', order: 'ASC' }
const ProductList: FC = () => {
    const { isWrite: isProductWrite } = useAppSelector(getScreenPermission("ProductStack"), shallowEqual)
    //State variable to use useAppDispatch hook
    const dispatch = useAppDispatch()
    //State variable to use width measurement from useWindowDimensions hook
    const { width, height } = useWindowDimensions();
    const productListData = useAppSelector((state) => state.product.posProductList)
    //State variable to store product list data to display
    const [productData, setProductData] = useState<ProductDataProps[]>(productListData?.value || [])
    //State variable to use useNavigation hook to navigate between pages
    const navigation = useAppNavigation();
    //State variable to store boolean value for page loading
    const [isLoading, setIsLoading] = useState<boolean>(!productListData?.value)
    //State variable to store boolean value for footer section loading
    const [isFooterLoading, setIsFooterLoading] = useState<boolean>(true)
    //State variable to store boolean value for sort popover
    const [isVisibleSort, setIsVisibleSort] = useState<boolean>(false)
    //State variable to store boolean value for filter popover
    const [isVisibleFilter, setIsVisibleFilter] = useState<boolean>(false)
    //State variable to store offset value for list
    const [offset, setOffset] = useState<number>(0);
    //State variable to store search text value
    const [searchText, setSearchText] = useState<string | null>()
    //State variable to store category id value
    const [filterValue, setFilterValue] = useState<{ medium: string | null, categoryId: number | null } | null>(null)
    const currencySymbol = useAppSelector(state => state.common.currentUser?.value?.purchasedCurrency?.currencySymbol)
    const format = (val: number | undefined) => val?.toLocaleString(undefined, { minimumFractionDigits: 0 })!;
    //State variable to store filter data for common filter
    const [filterData, setFilterData] = useState<filterItemsProps[]>([
        {
            id: "1",
            filterName: "Category",
            filterType: "dropdown",
            filterValue: [],
            initialSelected: "",
        },
        {
            id: "2",
            filterName: "Medium",
            filterType: "radio",
            filterValue: [
                {
                    value: 'WEB',
                    label: 'Admin',
                },
                {
                    value: 'POS',
                    label: 'POS',
                }
            ],
            initialSelected: ''
        },
    ])
    const [sortOrder, setSortOrder] = useState<{ field: string, order: string, id: number } | null>(DEFAULT_SORT)
    //State variable to store boolean value for refresh functionality
    const [isRefresh, setIsRefresh] = useState<boolean>(false)
    //State variable to store boolean value for delete popover
    const [deletePopover, setDeletePopover] = useState<{ isVisible: boolean, id: number | null }>({ id: null, isVisible: false })
    //variable to store filter count
    const [filtercount, setFilterCount] = useState(0)
    //variable to store delete list
    const [deleteList, setDeleteList] = useState<number[]>([])
    const didInit = useRef(false);
    const productDataRef = useRef(productData);
    const keyboardOpen = useKeyborad()

    useEffect(() => {
        productDataRef.current = productData;
    }, [productData]);

    const onMomentumScrollBegin = useRef(false);
    //Variable to store sort data
    const sortOptionsData: SortProps[] = [
        { name: 'Product name A-Z', id: 1, field: 'name', order: 'ASC' },
        { name: 'Product name Z-A', id: 2, field: 'name', order: 'DESC' },
        { name: "Recently created first", id: 3, field: 'created_at', order: 'DESC' },
        { name: "Recently created last", id: 4, field: 'created_at', order: 'ASC' },
        { name: "Recently updated first", id: 5, field: 'modified_at', order: 'DESC' },
        { name: "Recently updated last", id: 6, field: 'modified_at', order: 'ASC' }
    ]
    const moreOptionData: DialActions[] = [
        {
            title: "Ready-Made Products", icon: { name: "precision-manufacturing", type: "material", color: appColors?.labelColor }, onPress: () => {
                navigation.navigate("ReadymadeProducts")
            }
        },
        {
            title: "Bulk Edit", icon: { name: "edit", type: "material", color: appColors?.labelColor }, onPress: () => {
                navigation.navigate('BulkEdit')
            }
        },
    ]
    if (width < height && isProductWrite) {
        moreOptionData.push({
            title: "Add Product", icon: { name: "plus", type: "material-community", color: appColors?.labelColor }, onPress: () => {
                navigation.navigate('AddEditProduct')
            }
        })
    }
    const [quickEditVisible, setQuickEditVisible] = useState<QuickEditSetStateType>({ isVisible: false, productId: null })
    //State variable to store boolean value for the label print preview
    const [printLabelsVisible, setPrintLabelsVisible] = useState<boolean>(false)
    const [categories, setCategories] = useState<Code[]>([])

    //useEffect to render the when the productDataValue value from redux store changes
    useEffect(() => {
        if (filterValue) {
            setIsLoading(true)
            toggleOffsetValue()
        }
    }, [filterValue])

    useEffect(() => {
        if (productListData?.value) {
            setProductData(productListData.value.map(item => ({
                ...item,
                isSelected: deleteList.includes(item.id)
            })));
        }
    }, [productListData?.value, deleteList])

    useEffect(() => {
        const productUpdate = DeviceEventEmitter.addListener("PRODUCT_UPDATED", () => {
            // The list is refetched with the default sort after a product is added or edited, so show that sort as selected
            setSortOrder(DEFAULT_SORT)
            setFilterValue(null)
            DeviceEventEmitter?.emit("Product List Filter")
            setSearchText(null)
            setOffset(0);
        })
        return () => {
            productUpdate?.remove()
        }
    }, [])

    //useEffect triggered  when search data changes
    useEffect(() => {
        if (searchText) {
            setIsLoading(true)
            const debounceSearchText = setTimeout(() => {
                toggleOffsetValue()
            }, 1000);
            return () => { clearTimeout(debounceSearchText) }
        }
        else if (searchText == '') {
            setIsLoading(true)
            toggleOffsetValue()
        }
    }, [searchText])

    //Method for toggle offset value
    const toggleOffsetValue = () => {
        setOffset((preset) => (preset === 0 ? -1 : 0))
    }

    useEffect(() => {
        if (offset != undefined || sortOrder) {
            getAllPosProductList()
        }
    }, [offset, sortOrder])

    useEffect(() => {
        if (didInit.current) return;

        didInit.current = true;
        setDeleteList([]);
        setFilterValue(null);
        setOffset(0);

        getAllCategoriesName()
    }, []);

    useEffect(() => {
        const onBackPress = BackHandler.addEventListener("hardwareBackPress", () => {

            let selectedProducts = productDataRef.current?.filter(product => product.isSelected)

            if (selectedProducts?.length) {
                setProductData((prev) => prev?.map((product) => ({
                    ...product,
                    isSelected: false,
                })))
                setDeleteList([])
                return true;
            }
            return false;
        });
        return () => onBackPress.remove();
    }, [])

    const getAllCategoriesName = async () => {
        await dispatch(getAllPosCategoryData({ storeId: config.storeId })).unwrap().then(async (categoryResponse) => {
            const categories = categoryResponse.categories?.rows?.map((val) => ({
                value: val.id,
                label: val.categoryName,
            }));
            setFilterData((prev) => {
                let data: any = [...prev]
                data[0].filterValue = categories
                return data
            })
            setCategories(categories)
            // getAllPosProductList()
        }).catch((err) => {
            ZenToast({
                type: "error",
                message: "Failed to getting categories name, Please try again after some time!!"
            })
        })
    }

    //Method to dispatch getAllPosProductList api call
    const getAllPosProductList = async () => {
        const payload = {
            storeId: config.storeId,
            data: {
                limit: limit,
                offset: (!offset || offset == -1) ? 0 : offset,
                categoryId: filterValue?.categoryId || null,
                storeId: config.storeId,
                searchText: searchText ? searchText : '',
                sortOrder: sortOrder ? JSON.stringify(sortOrder) : JSON.stringify({ "field": "name", "order": "ASC" }),
                filterData: filterValue?.medium || null
            }
        }
        await dispatch(getAllPosProductListRedux(payload)).unwrap().catch((err) => {
            ZenToast({
                type: "error",
                message: "Failed to getting products, Please try again after some time!!"
            })
        }).finally(() => {
            setIsLoading(false)
            setIsRefresh(false)
            setIsFooterLoading(false)
        })
    }
    // Method to dispatch deleteProduct api call
    const deleteProduct = (id: number) => {
        setIsLoading(true);
        let payload = {
            storeId: config.storeId,
            id: id
        }
        dispatch(deleteProductRedux(payload)).unwrap().then((deleteResponse: DeleteProductResProps) => {
            if (deleteResponse) {
                setDeleteList([])
                ZenToast({
                    type: "success",
                    message: 'Product deleted successfully'
                })
            }
        }).catch((err) => {
            ZenToast({
                type: "error",
                message: "Failed to delete the product. Please try again after some time!!"
            })
        }).finally(() => {
            setTimeout(() => {
                setIsLoading(false)
            }, 1000);
        })
    }
    //Method for deleting product in bulk
    const deleteBulkProduct = () => {
        setIsLoading(true)
        let payLoad = {
            storeId: config.storeId,
            data: {
                id: deleteList
            }
        }
        dispatch(bulkDeleteProductRedux(payLoad)).unwrap().then((bulkDeleteResponse) => {
            if (bulkDeleteResponse?.deletedProductsCount) {
                setDeleteList([])
                ZenToast({
                    type: "success",
                    message: 'Products deleted successfully'
                })
            }
        }).catch((err) => {
            ZenToast({
                type: "error",
                message: "Failed to delete the products. Please try again after some time!!"
            })
        }).finally(() => {
            setTimeout(() => {
                setIsLoading(false)
            }, 1000);
        })
    }
    const handleLongPress = (event: ProductDataProps) => {
        const data = productData.map(item =>
            item.id === event?.id
                ? { ...item, isSelected: !item.isSelected }
                : item
        );
        setProductData(data);
    }
    //method for selecting and deselecting items
    const selectDeseletedItem = (id: number) => {
        setDeleteList((prev) => {
            let data = prev?.filter((item) => item != id)
            if (data?.length == prev?.length) {
                data?.push(id)
            }
            return data
        })
    }



    //Method used to perform leave confirmation
    const deleteConfirmation = (value: { name: string }) => {
        if (value?.name == 'Yes') {
            setDeletePopover((prev) => ({ ...prev, isVisible: false }));
            if (deletePopover?.id) {
                deleteProduct(deletePopover?.id)
            } else {
                deleteBulkProduct()
            }
        }
        else {
            setDeletePopover({ id: null, isVisible: false });
        }
    }

    const handleProductDelete = () => {
        if (deleteList?.length) {
            if (deleteList?.length > 1) {
                setDeletePopover({ id: null, isVisible: true })
            } else {
                setDeletePopover({ id: deleteList[0], isVisible: true })
            }
        } else {
            navigation.navigate('AddEditProduct')
        }
    }

    const RenderItem = ({ item, index }: { item: ProductDataProps, index: number }) => {
        return (
            <ThemeConsumer>
                {({ theme }) => (
                    <Pressable onPress={() => {
                        if ((width > height || deleteList?.length) && isProductWrite) {
                            selectDeseletedItem(item?.id)
                            handleLongPress(item)
                        }
                    }}
                        onLongPress={() => {
                            if (width < height && !deleteList?.length && isProductWrite) {
                                setDeleteList((prev) => [...prev, item?.id])
                                handleLongPress(item)
                            }
                        }}
                        style={({ pressed }) => [
                            theme.productListStyle.renderItemContainer,
                            item?.isSelected && theme.productListStyle.renderItemContainerSelected,
                            pressed && theme.productListStyle.cardPressed
                        ]}>
                        <View style={theme.productListStyle.itemInnerRow}>
                            <View style={theme.productListStyle.itemLeftCol}>
                                {(width > height || deleteList?.length > 0) && isProductWrite ?
                                    <View style={{ alignSelf: "center", marginRight: 4 }}>
                                        <ZenCheckBox
                                            onPress={() => { selectDeseletedItem(item?.id); handleLongPress(item) }}
                                            checked={item?.isSelected || false}
                                            checkedColor={theme?.primary}
                                            unCheckedColor={theme?.lightGrey} />
                                    </View> : undefined
                                }
                                <View style={theme.productListStyle.itemImageRow}>
                                    <Image
                                        resizeMode="cover"
                                        style={theme.productListStyle.itemImage}
                                        source={item?.featuredImage ? { uri: config?.AWSImageUrl + item?.featuredImage } : require("../../../assets/noimage.jpg")}
                                        defaultSource={require("../../../assets/noimage.jpg")}
                                        progressiveRenderingEnabled={true} />
                                    <View style={theme.productListStyle.itemDetailContainer}>
                                        <View style={theme.productListStyle.itemTextContainer}>
                                            <ZenText content={item?.name} size={theme.mediumFontSize} color={theme.lightDark} bold={true} numberOfLines={1} />
                                            <ZenText content={item?.priceRange?.price ? currencySymbol + format(item?.priceRange?.price) : currencySymbol + format(item?.priceRange?.startPrice!) + " - " + currencySymbol + format(item?.priceRange?.endPrice!)} color={theme.lightDark} size={theme?.smallFontSize} bold={true} />
                                            <ZenBadge
                                                badgeColor={item?.isVariants ? theme?.activeColor : theme.badgeLightColor}
                                                badgeName={item?.isVariants ? item?.variantsCount + " " + "Variant(s)" : "No Variant"}
                                                badgeTextSize={theme?.badgeFontSize}
                                                badgeTextStyle={{ fontFamily: theme?.bold }}
                                                badgeTextColor={item?.isVariants ? theme?.light : theme.lightDark}
                                            />
                                        </View>
                                    </View>
                                </View>
                                {!(width < height && deleteList?.length > 0) && (
                                    <View style={theme.productListStyle.itemActionContainer}>
                                        <TouchableOpacity onPress={() => { navigation.navigate('AddEditProduct', { productId: item?.id }) }} style={theme.productListStyle.actionButtonView} >
                                            <ZenIcon name={isProductWrite ? "pencil-outline" : "eye"} type="material-community" size={theme?.iconSize} color={theme?.lightDark} />
                                        </TouchableOpacity>

                                        <TouchableOpacity onPress={() => { setQuickEditVisible({ isVisible: true, productId: item.id }) }} style={theme.productListStyle.actionButtonEdit} >
                                            <ZenIcon name="playlist-edit" type="material-community" size={theme?.iconSize} color={theme?.primary} />
                                        </TouchableOpacity>

                                        {isProductWrite && <TouchableOpacity onPress={() => { setDeletePopover({ id: item?.id, isVisible: true }) }} style={theme.productListStyle.actionButtonDelete} >
                                            <ZenIcon name="delete-outline" type="material-community" size={theme?.iconSize} color={theme?.red} />
                                        </TouchableOpacity>}
                                    </View>
                                )}
                            </View>
                        </View>

                    </Pressable>
                )}
            </ThemeConsumer>
        )
    }

    return (
        <ThemeConsumer>
            {({ theme }) => (
                <KeyboardAvoidingView onStartShouldSetResponder={() => { Keyboard?.dismiss(); DeviceEventEmitter?.emit("isClicked"); return false }} behavior={Platform.OS === "ios" ? "padding" : undefined} enabled style={theme.productListStyle.containerView}>
                    <CommonHeader headerName={'Product List'} />
                    <ListToolbar
                        search={{
                            value: searchText ?? "",
                            onChange: (val) => setSearchText(val as string),
                            onCancel: () => setSearchText(null),
                            placeholder: 'Search products...',
                        }}
                        filter={{
                            count: filtercount ?? 0,
                            onPress: () => setIsVisibleFilter(true),
                        }}
                        sort={{
                            selectedId: sortOrder?.id,
                            onSelect: (item) => setSortOrder(item as { field: string, order: string, id: number }),
                            title: 'Sort by',
                            options: sortOptionsData

                        }}
                        buttons={isProductWrite ? [
                            {
                                title: 'Add Product',
                                onPress: () => navigation.navigate('AddEditProduct'),
                            },
                        ] : []}
                    />

                    {
                        isLoading ? <ZenLoader lottieType="Local" lottie={require('../../../assets/infinityLoader.json')} /> :
                            <View key={width > height ? "grid-view" : "list-view"} style={{ flex: 1, marginHorizontal: 10 }}>
                                <FlatList
                                    data={productData || []}
                                    renderItem={RenderItem}
                                    numColumns={width > height ? 2 : 1}
                                    key={width > height ? 'two-col' : 'one-col'}
                                    scrollEnabled
                                    keyExtractor={(_, index: number) => index.toString()}
                                    ListFooterComponentStyle={{ height: productListData?.value?.length ? 20 : undefined }}
                                    showsVerticalScrollIndicator={false}
                                    onMomentumScrollBegin={() => {
                                        onMomentumScrollBegin.current = true;
                                    }}
                                    onEndReached={() => {
                                        if (!onMomentumScrollBegin.current) return;
                                        if (isFooterLoading) return;
                                        if (Number(productListData?.count) > offset) {
                                            setIsFooterLoading(true);
                                            setOffset(prev => prev + limit);
                                        }
                                    }}
                                    refreshControl={<RefreshControl refreshing={isRefresh} onRefresh={() => {
                                        setIsRefresh(true)
                                        setProductData(prev => {
                                            return prev?.map(product => {
                                                return {
                                                    ...product,
                                                    isSelected: false
                                                }
                                            })
                                        })
                                        setFilterValue(null)
                                        DeviceEventEmitter.emit("Product List Filter")
                                        setSearchText(null)
                                        toggleOffsetValue()
                                        if (deleteList?.length) {
                                            setDeleteList([])
                                        }
                                    }} />}
                                    contentContainerStyle={{
                                        paddingBottom: productData?.length ? 10 : undefined,
                                        flexGrow: productData?.length ? undefined : 1,
                                        // gap: (productData?.length && width < height) ? 10 : undefined,
                                    }}
                                    columnWrapperStyle={width > height ? { columnGap: 10 } : undefined}
                                    ListEmptyComponent={<ZenEmptyComponent />}
                                    onEndReachedThreshold={0.01}
                                    ListFooterComponent={isFooterLoading ? <View style={theme.productListStyle.listFooterLoader}>
                                        <ZenLoader size={"small"} color={theme?.primary} />
                                    </View> : <></>}
                                    ListHeaderComponent={() => {
                                        if (deleteList?.length) {
                                            return (
                                                <View style={theme.productListStyle.selectAllHeader}>
                                                    <View style={theme.productListStyle.selectAllLeft}>
                                                        <ZenCheckBox
                                                            title={deleteList?.length == productData?.length ? 'Deselect All' : 'Select All'}
                                                            containerStyle={theme.productListStyle.checkBoxContainer}
                                                            checkedColor={theme.primary}
                                                            onPress={() => {
                                                                if (deleteList?.length === productData?.length) {
                                                                    // UNSELECT ALL
                                                                    const updated = productData.map(item => ({
                                                                        ...item,
                                                                        isSelected: false,
                                                                    }));

                                                                    setProductData(updated);
                                                                    setDeleteList([]);
                                                                } else {
                                                                    // SELECT ALL
                                                                    const updated = productData.map(item => ({
                                                                        ...item,
                                                                        isSelected: true,
                                                                    }));

                                                                    const ids = updated.map(item => item.id);

                                                                    setProductData(updated);
                                                                    setDeleteList(ids);
                                                                }
                                                            }}
                                                            size={20}
                                                            checked={deleteList?.length == productData?.length} />
                                                    </View>

                                                    <View style={theme?.customerListStyle?.optionContainer}>
                                                        {/* Prints a label for every selected variant that has a SKU */}
                                                        <TouchableOpacity
                                                            style={theme?.customerListStyle?.optionInnerContainer}
                                                            accessibilityRole="button"
                                                            accessibilityLabel={`Open label printer for ${deleteList?.length} selected products`}
                                                            onPress={() => { setPrintLabelsVisible(true) }}>
                                                            <ZenIcon type="material-community" name="barcode" size={22} color={theme.primary} />
                                                            <ZenText content={'Label Printer'} style={[theme?.customerListStyle?.optionTextStyle, { color: theme?.primary }]} />
                                                        </TouchableOpacity>
                                                        {width > height ? <TouchableOpacity style={theme?.customerListStyle?.optionInnerContainer} onPress={() => { setDeletePopover({ id: null, isVisible: true }) }}>
                                                            <ZenIcon type="material-community" name="delete" size={22} color={theme.red} />
                                                            <ZenText content={'Delete'} style={[theme?.customerListStyle?.optionTextStyle, { color: theme?.red }]} />
                                                        </TouchableOpacity> : null}
                                                    </View>
                                                </View>
                                            )
                                        }
                                    }}
                                />
                            </View>
                    }
                    {(!keyboardOpen && !isLoading) ? deleteList?.length && isProductWrite ? (width < height ? <Pressable style={[
                        theme.productListStyle.fabButton,
                        { backgroundColor: deleteList?.length ? theme.red : theme.primary }
                    ]} onPress={handleProductDelete}>
                        <ZenIcon type="material-community" name={deleteList?.length ? "delete-outline" : "plus"} size={width > height ? 25 : 22} color={theme.light} />
                    </Pressable> : undefined) : <ZenSpeedDial
                        icon={{ name: 'dots-three-vertical', type: "entypo", color: '#fff', size: width > height ? 22 : 18 }}
                        actions={moreOptionData}
                        backgroundColor={theme?.primary}
                        menuBackground={theme?.light}
                        titleColor={theme?.lightDark}
                    /> : undefined}
                    {/* Popover for delete confirmation */}
                    <Popover isVisible={deletePopover?.isVisible} popoverStyle={{ width: width > height ? 500 : 350, borderRadius: 10 }}>
                        <CommonDialogBox type="DELETE" replaceValue="" confirmation={deleteConfirmation} />
                    </Popover>
                    <ZenFilterModal
                        setVisible={setIsVisibleFilter}
                        visible={isVisibleFilter}
                        filterName="Product List Filter"
                        filterItems={filterData}
                        setFilterItems={setFilterData}
                        filterCount={filtercount}
                        setFilterCount={setFilterCount}
                        title="Product List Filter"
                        isSingleExpand={false}
                        onConfirm={(val) => {
                            setIsLoading(true)
                            if (val?.values?.["Medium"] || val?.values?.["Category"]) {
                                let data
                                if (val?.values?.["Medium"]) {
                                    data = JSON.stringify({ medium: val?.values?.["Medium"] })
                                }
                                setFilterValue({ medium: data || null, categoryId: val?.values?.["Category"] || null })
                            }
                            else {
                                setFilterValue(null)
                                toggleOffsetValue()
                            }
                        }}
                    />

                    <QuickProductEdit setModalVisible={setQuickEditVisible} modalVisible={quickEditVisible} categoryOptions={categories} />

                    <PrintProductLabels
                        visible={printLabelsVisible}
                        onClose={() => setPrintLabelsVisible(false)}
                        products={productData || []}
                        selectedIds={deleteList}
                    />
                </KeyboardAvoidingView>
            )}
        </ThemeConsumer>


    )
}

export default ProductList